import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { ROUTES } from '@/utils/constants'
import { cardsApi } from '@/api/cardsApi'
import { useEnumsStore } from '@/stores/enumsStore'
import { useAuthStore } from '@/stores/authStore'

export type CreateCardModalResult = 'created' | 'cancelled'

interface CreateCardModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (result: CreateCardModalResult) => void
  userId?: number
}

type Step = 'input' | 'not_found'

const ATTRIBUTE_KEYS = [
  'transcription',
  'translation',
  'part_of_speech',
  'type',
  'example_sentence',
  'synonym',
  'antonym',
  'note',
] as const

const ENUM_ATTRIBUTE_KEYS = ['part_of_speech', 'type'] as const

export function CreateCardModal({
  open,
  onClose,
  onSuccess,
  userId,
}: CreateCardModalProps) {
  const { t } = useTranslation()
  const { enums, loadEnums } = useEnumsStore()
  const roles = useAuthStore((s) => s.user?.roles) ?? []
  const requiresTeacherAttributes = roles.some((r) => r.slug === 'teacher' || r.slug === 'admin')
  const [text, setText] = useState('')
  useEffect(() => {
    if (open) loadEnums().catch(() => {})
  }, [open, loadEnums])
  const [step, setStep] = useState<Step>('input')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subscriptionError, setSubscriptionError] = useState(false)
  const [attributes, setAttributes] = useState<Array<{ key: string; value: string }>>(
    ATTRIBUTE_KEYS.map((key) => ({ key, value: '' }))
  )

  const reset = () => {
    setText('')
    setStep('input')
    setError(null)
    setSubscriptionError(false)
    setAttributes(ATTRIBUTE_KEYS.map((key) => ({ key, value: '' })))
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleAdd = async () => {
    const trimmed = text.trim()
    if (!trimmed) {
      setError(t('cards.validation.text_required'))
      return
    }
    setError(null)
    setLoading(true)
    try {
      const payload: { text: string; user_id?: number } = { text: trimmed }
      if (userId) payload.user_id = userId
      const result = await cardsApi.checkWord(payload)
      if (result.found && result.card_exists) {
        setError(t('cards.errors.already_exists'))
      } else if (result.found && !result.card_exists) {
        await cardsApi.createCard(payload)
        onSuccess('created')
        handleClose()
      } else {
        if (!requiresTeacherAttributes) {
          await cardsApi.createCard(payload)
          onSuccess('created')
          handleClose()
          return
        }
        setStep('not_found')
      }
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { message?: string; error?: string } } }
      if (e.response?.status === 422) {
        setError(e.response?.data?.message ?? t('cards.errors.already_exists'))
      } else {
        setError(e.response?.data?.message ?? t('cards.errors.create_failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreateWithAttributes = async () => {
    setError(null)
    if (requiresTeacherAttributes) {
      const transcription = attributes.find((a) => a.key === 'transcription')?.value.trim() ?? ''
      const translation = attributes.find((a) => a.key === 'translation')?.value.trim() ?? ''
      if (!transcription || !translation) {
        setError(t('cards.validation.transcription_and_translation_required'))
        return
      }
    }
    setLoading(true)
    const attrs = attributes
      .filter((a) => a.value.trim())
      .map((a) => ({ key: a.key, value: a.value.trim() }))
    try {
      const payload: { text: string; attributes?: Array<{ key: string; value: string }>; user_id?: number } = {
        text: text.trim(),
      }
      if (attrs.length) payload.attributes = attrs
      if (userId) payload.user_id = userId
      await cardsApi.createCard(payload)
      onSuccess('created')
      handleClose()
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } }
      const isSubLimit = e.response?.status === 403 && (e.response?.data?.error === 'subscription_limit_exceeded' || e.response?.data?.error === 'subscription_parameter_denied')
      if (isSubLimit) {
        setError(e.response?.data?.message ?? t('subscription.limit_exceeded'))
        setSubscriptionError(true)
      } else {
        setError(e.response?.data?.message ?? t('cards.errors.create_failed'))
        setSubscriptionError(false)
      }
    } finally {
      setLoading(false)
    }
  }

  const updateAttr = (index: number, value: string) => {
    setAttributes((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], value }
      return next
    })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('cards.create_card')}</DialogTitle>
      <DialogContent>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => { setError(null); setSubscriptionError(false) }}>
              {error}
              {subscriptionError && (
                <Box sx={{ mt: 1 }}>
                  <Button component={Link} to={ROUTES.PROFILE_SUBSCRIPTION} variant="outlined" size="small">
                    {t('subscription.goToSubscription')}
                  </Button>
                </Box>
              )}
            </Alert>
          </Box>
        )}

        {step === 'input' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('cards.word')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </Box>
        )}

        {step === 'not_found' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('cards.messages.word_not_found')}
            </Typography>
            {ATTRIBUTE_KEYS.map((key, index) => {
              const isEnum = ENUM_ATTRIBUTE_KEYS.includes(key as (typeof ENUM_ATTRIBUTE_KEYS)[number])
              const label =
                key === 'part_of_speech'
                  ? t('cards.filter.part_of_speech')
                  : key === 'type'
                    ? t('cards.filter.type')
                    : key === 'transcription'
                      ? t('cards.transcription')
                      : key === 'translation'
                        ? t('cards.translation')
                        : key.replace(/_/g, ' ')
              if (isEnum && key === 'part_of_speech') {
                return (
                  <FormControl key={key} fullWidth size="small">
                    <InputLabel>{label}</InputLabel>
                    <Select
                      value={attributes[index]?.value ?? ''}
                      label={label}
                      onChange={(e) => updateAttr(index, e.target.value)}
                    >
                      <MenuItem value="">{t('cards.filter.all')}</MenuItem>
                      {(enums?.PartOfSpeech ?? []).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              }
              if (isEnum && key === 'type') {
                return (
                  <FormControl key={key} fullWidth size="small">
                    <InputLabel>{label}</InputLabel>
                    <Select
                      value={attributes[index]?.value ?? ''}
                      label={label}
                      onChange={(e) => updateAttr(index, e.target.value)}
                    >
                      <MenuItem value="">{t('cards.filter.all')}</MenuItem>
                      {(enums?.WordType ?? []).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              }
              return (
                <TextField
                  key={key}
                  fullWidth
                  size="small"
                  label={label}
                  value={attributes[index]?.value ?? ''}
                  onChange={(e) => updateAttr(index, e.target.value)}
                  required={
                    requiresTeacherAttributes && (key === 'transcription' || key === 'translation')
                  }
                />
              )
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 'input' && (
          <>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={loading || !text.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {t('cards.add')}
            </Button>
          </>
        )}
        {step === 'not_found' && (
          <>
            <Button onClick={() => setStep('input')}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleCreateWithAttributes}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {t('cards.create_word_and_add_card')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
